import React, { useEffect, useState } from 'react';

// Components
import { Checkbox } from '@/views/components/inputs';
import { PrimaryButton } from '@/views/components/buttons';

// Libraries
import PerfectScrollbar from 'react-perfect-scrollbar';

// Types
import { SelectedDataPanelProps } from '@/types/data-table';
import Nullable from '@/types/nullable';

// Utils
import { formatLocalizedString, formatMessage } from '@/utils/i18n';
import get from 'lodash/get';

function SelectedDataPanel<T extends object>({
  selectedData,
  multiselectActionsOptions,
  clearSelection,
  disabled,
}: SelectedDataPanelProps<T>): Nullable<JSX.Element> {
  const options = multiselectActionsOptions;
  const data = options?.overrideSelectedData ?? selectedData;
  const [allowedActions, setAllowedActions] = useState(options?.actions ?? []);

  useEffect(() => {
    setAllowedActions(options?.actions.filter(action => (action.isCurrentlyAllowed ? action.isCurrentlyAllowed(data) : true)) ?? []);
  }, [options?.actions, data, setAllowedActions]);

  const renderAllowedActionsButtons = (): JSX.Element => (
    <div className="action-buttons">
      {allowedActions.map((action, index) => (
        <PrimaryButton
          key={index}
          className="primary-v2"
          compact
          icon={action.icon}
          loading={action.loading}
          onClick={() => {
            action.onClick(data, clearSelection);
          }}
          disabled={disabled}
        >
          {action.label}
        </PrimaryButton>
      ))}
    </div>
  );

  const renderClearSelectionCheckbox = (): JSX.Element => (
    // TODO Add a tooltip
    <Checkbox ariaLabel={formatMessage('Common.Accessibility.ClearSelection')} selected={false} onChange={clearSelection} unselect disabled={disabled} />
  );

  const renderTitle = (): JSX.Element => {
    const title = options ? formatMessage(options.selectedTitle, { count: data.length }) : null;
    return <div className="selection-header-title">{title}</div>;
  };

  const renderSelectedLabels = (): Nullable<JSX.Element> => {
    if (!options?.labelAccessor || !data.length) {
      return null;
    }

    return (
      <PerfectScrollbar className="selected-labels">
        {data.map((s, i) => (
          <div key={get(s, '_id') as string} className="selected-label">
            <span>{formatLocalizedString(get(s, options.labelAccessor!))}</span>
          </div>
        ))}
      </PerfectScrollbar>
    );
  };

  const panelIsNeeded = options && data.length > 0;

  if (!panelIsNeeded) {
    return null;
  }

  return (
    <div className="selection-header-v2">
      <div className="first-row">
        <div className="checkbox-and-title">
          {renderClearSelectionCheckbox()}
          {renderTitle()}
        </div>
        {renderAllowedActionsButtons()}
      </div>
      {renderSelectedLabels()}
    </div>
  );
}

export default SelectedDataPanel;
